/**
 * Copyright (c) 2024 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { useEffect, useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { useDispatch } from 'react-redux';
import { useVideoContext } from '../../../../contexts/videoProvider/videoContext';
import { addGenericErrorNotification, addNotification } from '../../../../actions/pageActions';
import { NotificationItem } from '@components/common/Notifications/NotificationItem';

export const useVideoAudioStream = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const { room: zoomSession, videoProvider, getLocalVideoTrack, getLocalAudioTrack } = useVideoContext();

  useEffect(() => {
    const startVideoAudioStream = async () => {
      if (!zoomSession) {
        // audio and video streams should only be started after zoomClient.join() is called
        // so, we will wait for videoProvider.connect() to complete before starting the streams
        return;
      }

      try {
        const localVideoTrack = await getLocalVideoTrack();
        await videoProvider.streamLocalVideo(localVideoTrack.deviceId);
        const localAudioTrack = await getLocalAudioTrack();
        await videoProvider.streamLocalAudio(localAudioTrack.deviceId);
      } catch (err: any) {
        const errMessage = err.message || 'Error starting video/audio stream';
        const notification: NotificationItem.PropsWithoutId = {
          type: 'error',
          iconType: 'error',
          content: `${errMessage}. Please check for camera and microphone permissions.`,
          shouldAutoRemove: true,
        };
        // eslint-disable-next-line no-console
        console.error(errMessage, err);
        addNotification(notification)(dispatch);
      } finally {
        setIsLoading(false);
      }
    };

    startVideoAudioStream();
    return () => {
      if (zoomSession) {
        videoProvider.stopLocalVideo();
        videoProvider.stopLocalAudio();
      }
    };
  }, [zoomSession, videoProvider, dispatch]);

  useEffect(() => {
    const permissionDeniedCallback = (device: string) => {
      datadogRum.addAction('[ZOOM] User denied the permission');
      const errMessage = `${device} access denied. Please enable permissions and refresh the page.`;
      addGenericErrorNotification(errMessage, true)(dispatch);
    };

    videoProvider.onDevicePermissionsChanged(permissionDeniedCallback);
  }, []);

  return { isLoading };
};
